import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { TableSkeleton } from "../common/Skeletons/TableSkeleton";
import React, {
  Suspense,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useMedia } from "react-media";
import { connect } from "react-redux";
import clsx from "clsx";
import "../common/Tables/TradeAlgoTable.scss";
import "./InfiniteScrollLiveOptionsTable.scss";
import CustomToolbar from "../common/Tables/CustomToolbar";
import { addUserSettingsLoad } from "../../appRedux/ducks/userSettings";

const mediaQuery = {
  isMobile: "screen and (max-width: 991px)",
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.primary.text,
    borderBottomColor: "rgb(60, 64, 67)",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderBottomColor: "rgb(60, 64, 67)",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function MobileTable({ rows = [], columns, loading, sortModel }) {
  if (!rows.length && !loading) {
    return (
      <div
        className={clsx(
          "tradealgo-table-no-data",
          "MuiDataGrid-windowContainer",
        )}
        style={{ top: "56px", overflowY: "hidden" }}
      >
        No Rows
      </div>
    );
  }
  return (
    <TableContainer elevation={0}>
      {loading ? (
        <TableSkeleton />
      ) : (
        <Table
          className={"tradealgo-table-background tradealgo-mobile-table"}
          sx={{ minWidth: 700 }}
          aria-label="customized table"
        >
          <TableHead>
            <TableRow>
              {columns.map((col, index) => {
                if (col.hide) return null;
                return (
                  <StyledTableCell
                    className={"tradealgo-table-text"}
                    key={index}
                  >
                    {col.headerName}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...rows]
              ?.sort((a, b) => b[sortModel] - a[sortModel])
              ?.map((row, index) => {
                return (
                  <StyledTableRow key={index}>
                    {columns?.map((col, j) => {
                      if (col.hide) return null;
                      if (col.renderCell) {
                        let params = {
                          value: row[columns[j]?.field],
                          row,
                        };
                        return (
                          <StyledTableCell key={j} component="th" scope="row">
                            {col?.renderCell(params)}
                          </StyledTableCell>
                        );
                      }
                      return (
                        <StyledTableCell key={index} component="th" scope="row">
                          {typeof row[columns[j].field] === "object"
                            ? ""
                            : row[columns[j].field]}
                        </StyledTableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
}

const DesktopTable = ({
  rows,
  columns,
  loading,
  sortModel,
  setSortModel,
  tableClassName,
  hasRowClassName,
  rowClassName,
  customToolbar = false,
  tableID = "",
  userSettings,
  setUserSettings,
  threshold,
  hasMore = true,
  // Infinite scroll props
  onLoadMore, // Function to call when more data is needed
}) => {
  const apiRef = useGridApiRef();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [columnsOrder, setColumnsOrder] = useState([]);
  const [density, setDensity] = useState("standard");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [tableHeight, setTableHeight] = useState(600); // State for dynamic height
  const lastRequestTimeRef = useRef(0);

  // Scroll detection for infinite scroll
  const scrollContainerRef = useRef(null);
  const hasTriggeredLoadRef = useRef(false); // Prevent multiple triggers for same scroll session
  const scrollThrottleRef = useRef(null); // For throttling scroll events

  // Throttled infinite scroll handler
  const handleScrollThrottled = useCallback(
    (event) => {
      // Don't trigger if already loading or no more data
      if (loading || isLoadingMore || !hasMore || !onLoadMore) {
        return;
      }

      const container = event.target;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Calculate how close to bottom (trigger when 90% scrolled)
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      const shouldLoadMore = scrollPercentage > 0.9;

      // Additional check: only trigger if user has actually scrolled down
      // This prevents socket data from triggering load more when user is at bottom
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100; // Within 100px of bottom

      if (shouldLoadMore && isNearBottom && !hasTriggeredLoadRef.current) {
        // Prevent multiple triggers until data is loaded
        hasTriggeredLoadRef.current = true;

        // Additional debouncing check
        const now = Date.now();
        const timeSinceLastRequest = now - lastRequestTimeRef.current;
        const minInterval = 3000; // Increased to 3 seconds to prevent rapid requests

        if (timeSinceLastRequest < minInterval) {
          hasTriggeredLoadRef.current = false; // Reset flag
          return;
        }

        // Store current data length to detect if this is triggered by socket data
        const currentDataLength = rows.length;

        // Additional safety: don't trigger if data length just changed (indicates socket update)
        setTimeout(() => {
          // Check if data length changed while we were waiting (socket update)
          if (rows.length !== currentDataLength) {
            hasTriggeredLoadRef.current = false;
            return;
          }

          setIsLoadingMore(true);
          lastRequestTimeRef.current = now;
          onLoadMore(); // Call the API function
        }, 100); // Small delay to catch rapid socket updates
      }
    },
    [hasMore, loading, isLoadingMore, onLoadMore, rows.length],
  );

  // Throttle the scroll handler to only run every 250ms
  const handleScroll = useCallback(
    (event) => {
      if (scrollThrottleRef.current) {
        clearTimeout(scrollThrottleRef.current);
      }

      scrollThrottleRef.current = setTimeout(() => {
        handleScrollThrottled(event);
      }, 250); // 250ms throttle
    },
    [handleScrollThrottled],
  );

  // Reset trigger flag when new data arrives or loading completes
  useEffect(() => {
    if (!loading && !isLoadingMore) {
      hasTriggeredLoadRef.current = false;
    }

    // Sync local loading state with global loading state
    if (!loading && isLoadingMore) {
      setIsLoadingMore(false);
      hasTriggeredLoadRef.current = false; // Reset trigger when loading completes
    }
  }, [loading, isLoadingMore, rows.length]);

  function handleColumnOrderChange() {
    const newOrder = apiRef.current.state.columns.orderedFields;
    if (customToolbar && tableID) {
      setUserSettings({
        ...userSettings,
        columnsOrder: {
          ...userSettings.columnsOrder,
          [tableID]: newOrder,
        },
      });
      setColumnsOrder(newOrder);
    }
  }

  function handleColumnVisibilityModelChange(model) {
    if (customToolbar && !!tableID) {
      setUserSettings({
        ...userSettings,
        colVisibilityModel: {
          ...userSettings?.colVisibilityModel,
          [tableID]: model,
        },
      });
      setColumnVisibilityModel(model);
    }
  }

  const handleDensityChange = (newDensity) => {
    if (customToolbar && tableID) {
      setUserSettings({
        ...userSettings,
        density: {
          ...userSettings.density,
          [tableID]: newDensity,
        },
      });
      setDensity(newDensity);
    }
  };

  // Calculate optimal table height for full page view
  const calculateTableHeight = useCallback(() => {
    // Calculate available height: viewport minus only essential UI elements
    const viewportHeight = window.innerHeight;
    const topNavHeight = 50; // Top navigation bar (reduced)
    const summaryHeight = 40; // Circular summary widgets (further reduced)
    const filtersHeight = 40; // Filter sliders and controls (further reduced)
    const statusHeight = 10; // Small status indicators (minimal)
    const padding = 5; // Minimal padding (further reduced)

    const usedHeight =
      topNavHeight + summaryHeight + filtersHeight + statusHeight + padding;
    const availableHeight = viewportHeight - usedHeight;

    // Use almost all available space, with a sensible minimum
    const minHeight = 300; // Minimum ~6 rows

    // Be even more aggressive - use 98% of available height
    const aggressiveHeight = Math.floor(availableHeight);

    // Return the larger of aggressive height or minimum
    return Math.max(aggressiveHeight, minHeight);
  }, []);

  // Update table height on mount and window resize
  useEffect(() => {
    const updateHeight = () => {
      const newHeight = calculateTableHeight();
      setTableHeight(newHeight);
    };

    // Set initial height
    updateHeight();

    // Add resize listener
    window.addEventListener("resize", updateHeight);

    // Cleanup
    return () => window.removeEventListener("resize", updateHeight);
  }, [calculateTableHeight]);

  // Reset loading state when external loading changes
  useEffect(() => {
    if (!loading && isLoadingMore) {
      setIsLoadingMore(false);
    }
  }, [loading, isLoadingMore]);

  useEffect(() => {
    if (
      userSettings &&
      userSettings?.density &&
      userSettings?.density[tableID]
    ) {
      setDensity(userSettings.density[tableID]);
    }
    if (
      !!userSettings &&
      !!userSettings?.colVisibilityModel &&
      !!userSettings?.colVisibilityModel[tableID]
    ) {
      setColumnVisibilityModel(userSettings?.colVisibilityModel[tableID]);
    }
    if (
      userSettings &&
      userSettings?.columnsOrder &&
      userSettings?.columnsOrder[tableID]
    ) {
      setColumnsOrder(userSettings?.columnsOrder[tableID]);
    }
  }, [userSettings, tableID]);

  const orderedColumns = useMemo(() => {
    if (columnsOrder && columnsOrder.length > 0) {
      const reorderedColumns = columnsOrder
        .map((field) => columns.find((col) => col.field === field))
        .filter(Boolean);

      const newColumns = columns.filter(
        (col) => !columnsOrder.includes(col.field),
      );

      return [...reorderedColumns, ...newColumns];
    } else {
      return columns;
    }
  }, [columnsOrder, columns]);

  const visibleColumns = useMemo(() => {
    return orderedColumns.filter(
      (col) => columnVisibilityModel[col.field] !== false,
    );
  }, [orderedColumns, columnVisibilityModel]);

  const visibleColumnsCount = visibleColumns.length;
  const adjustedColumns = useMemo(() => {
    if (visibleColumnsCount < threshold) {
      return orderedColumns.map((col) => {
        return {
          ...col,
          flex: 1,
          minWidth: undefined,
        };
      });
    } else {
      return orderedColumns;
    }
  }, [orderedColumns, visibleColumnsCount, threshold]);

  // Attach scroll listener to DataGrid virtual scroller
  useEffect(() => {
    const dataGridElement = apiRef.current?.rootElementRef?.current;
    if (!dataGridElement) return;

    // Find the virtual scroller element
    const virtualScroller = dataGridElement.querySelector(
      ".MuiDataGrid-virtualScroller",
    );
    if (!virtualScroller) return;

    // Add scroll listener
    virtualScroller.addEventListener("scroll", handleScroll, { passive: true });
    scrollContainerRef.current = virtualScroller;

    // Cleanup
    return () => {
      if (virtualScroller) {
        virtualScroller.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll, rows.length]); // Re-attach when rows change

  if (loading && rows.length === 0) {
    return <TableSkeleton />;
  }

  return (
    <div style={{ width: "100%" }}>
      <div
        className="virtualized-table-container"
        style={{
          height: tableHeight,
          width: "100%",
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          columnVisibilityModel={columnVisibilityModel}
          density={density}
          onDensityChange={handleDensityChange}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onColumnOrderChange={handleColumnOrderChange}
          rows={rows}
          className={`${tableClassName} virtualized-infinite-scroll-table`}
          columns={adjustedColumns}
          initialState={{
            columns: {
              columnVisibilityModel: columnVisibilityModel,
            },
            pinnedColumns: {
              left: [columns.length > 0 ? columns[0]?.field || "" : ""],
            },
          }}
          slots={{
            toolbar: customToolbar ? CustomToolbar : "",
          }}
          // CORE VIRTUALIZATION SETTINGS - SIMPLIFIED
          autoHeight={false} // CRITICAL: Must be false
          height={tableHeight} // CRITICAL: Fixed height enables virtualization
          rowHeight={52} // CRITICAL: Consistent row height
          // CORE PERFORMANCE SETTINGS
          hideFooter={true}
          disableColumnMenu={true}
          disableRowSelectionOnClick={true}
          sortingOrder={["asc", "desc"]}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            if (
              model[0]?.field !== sortModel[0]?.field ||
              model[0]?.sort !== sortModel[0]?.sort
            ) {
              setSortModel(model);
            }
          }}
          getRowClassName={hasRowClassName ? rowClassName : ""}
        />
      </div>

      {/* No more data indicator */}
      {!hasMore && rows.length > 0 && (
        <div
          style={{
            padding: "15px",
            textAlign: "center",
            background: "#e8f5e8",
            fontSize: "14px",
            color: "#2e7d32",
            border: "1px solid #c8e6c9",
            borderTop: "none",
          }}
        >
          All data loaded ({rows.length} total{" "}
          {rows.length === 1 ? "row" : "rows"})
        </div>
      )}
    </div>
  );
};

const InfiniteScrollLiveOptionsTable = ({
  tableClassName = "",
  rows,
  columns,
  loading = false,
  sortModel,
  setSortModel,
  hasRowClassName = false,
  rowClassName = "",
  customToolbar = false,
  tableID = "",
  setUserSettings,
  userSettings,
  threshold = 0,
  hasMore = true,
  onLoadMore, // Function to call for loading more data
}) => {
  const { isMobile } = useMedia({ queries: mediaQuery });

  return (
    <Suspense fallback={<TableSkeleton />}>
      {isMobile ? (
        <MobileTable
          sortModel={sortModel[0]?.field}
          loading={loading}
          rows={rows}
          columns={columns}
        />
      ) : (
        <div className={"tradealgo-table-datagrid"}>
          <div style={{ height: "fit-content", width: "100%" }}>
            <DesktopTable
              customToolbar={customToolbar}
              sortModel={sortModel}
              loading={loading}
              rows={rows}
              columns={columns}
              tableClassName={tableClassName}
              setSortModel={setSortModel}
              hasRowClassName={hasRowClassName}
              rowClassName={rowClassName}
              tableID={tableID}
              setUserSettings={setUserSettings}
              userSettings={userSettings}
              threshold={threshold}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
            />
          </div>
        </div>
      )}
    </Suspense>
  );
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
  userSettings: state.userSettings.userSettings,
});

const dispatchToProps = (dispatch) => ({
  setUserSettings: (settings, firstRender) =>
    dispatch(addUserSettingsLoad(settings, firstRender)),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(InfiniteScrollLiveOptionsTable);
